<template>
  <div class="fragment-upload">
    <el-upload
      ref="uploader"
      action=""
      :class="uploadClass"
      :name="name"
      :data="uploadData"
      :show-file-list="false"
      :accept="accept"
      :multiple="multiple"
      :headers="headers"
      :file-list="files"
      :drag="drag"
      :limit="1"
      :before-upload="handleBeforeUpload"
      :on-exceed="onExceed"
      :on-preview="onPreview"
      :on-remove="onRemove"
      :with-credentials="withCredentials"
    >
      <template v-slot:tip>
        <slot name="tip" />
      </template>
      <slot />
    </el-upload>
    <div class="upload-status">
      <el-progress v-if="uploading" :percentage="percentage" :format="format"></el-progress>
    </div>
  </div>
</template>

<script>
import SparkMD5 from 'spark-md5'
import { STATUS_CODE } from '@/utils/constants'

function noop() {}

export default {
  name: 'FragmentUploader',
  props: {
    headers: Object,
    withCredentials: Boolean,
    multiple: Boolean,
    drag: Boolean,
    disabled: Boolean,
    accept: String,
    uploadClass: String,
    name: {
      default: 'file',
      type: String,
    },
    fileList: {
      default() {
        return []
      },
      type: Array
    },
    beforeUpload: Function,
    onRemove: {
      type: Function,
      default: noop
    },
    onPreview: {
      type: Function
    },
    onSuccess: {
      type: Function,
      default: noop
    },
    onExceed: {
      type: Function,
      default: noop
    },
    onProgress: {
      type: Function,
      default: noop
    },
    onError: {
      type: Function,
      default: noop
    },
    data: Object
  },
  data() {
    return {
      uploading: false,
      percentage: 0,
      fragmentSize: 1048576, // 1MB
      fragmentIndex: 0,
      files: this.$props.fileList,
      uploadData: {
        ...this.$props.data,
        md5: ''
      }
    }
  },
  watch: {
    '$props.data'(newVal) {
      this.uploadData = {
        ...newVal,
        md5: this.uploadData.md5
      }
    },
    fileList(newVal) {
      this.files = newVal
    }
  },
  methods: {
    format(percentage) {
      if (percentage === 100) {
        return '检测中...'
      } else if (percentage === 0) {
        return '解析文件中...'
      } else {
        return `${percentage}%`
      }
    },
    async handleBeforeUpload(file) {
      this.uploading = true
      // console.log(file)
      // return

      try {
        const md5 = await this.calMediaMD5(file)
        file.md5 = md5
        let beforeResult // eslint-disable-line
        if (this.beforeUpload) {
          beforeResult = await this.beforeUpload(file) // 如果返回的是 reject promise, 会进入到 catch
          if (beforeResult === false) return Promise.reject()
        }
        this.startUpload(file) // 这是一个异步的过程，不需要等待

        return Promise.reject()
      } catch (error) {
        console.error(error)
        this.onError(error, file)
        this.uploading = false
        return Promise.reject(error)
      }
    },
    async startUpload(file) {
      try {
      // http://impai.tmeoa.com/project/5238/interface/api/15730
      // 获取文件信息，是否曾经上传过部分
        const uploadInfo = await this.$http({
          method: 'post',
          url: 'mv/get_upload_mv_info',
          data: {
            md5: file.md5,
            fileSize: file.size
          }
        })

        // 0:初始化 1：上传中 2：部分上传完成 3：全部上传完成 4：上传失败
        let fileInfo
        if (uploadInfo.status === 3) {
          fileInfo = uploadInfo
        } else {
          this.fragmentSize = uploadInfo.fragmentSize
          this.fragmentIndex = uploadInfo.fragmentIndex
          fileInfo = await this.fragmentUpload(file)
        }
        file.url = fileInfo.url
        file.mediaUrl = fileInfo.url
        file.mediaPath = fileInfo.file_id
        const f = Object.assign({}, file)
        f.name = file.name
        f.size = file.size
        f.type = file.type
        this.files = [f]
        this.onSuccess(f)
        this.uploading = false
      } catch (error) {
        console.error(error)
        this.onError(error, file)
      }
    },

    fragmentUpload(file) {
      const blobSlice = File.prototype.slice || File.prototype.mozSlice || File.prototype.webkitSlice
      const chunkSize = this.fragmentSize
      const chunks = Math.ceil(file.size / chunkSize)
      const fileReader = new FileReader()
      let lastFragment = 0
      let currentChunk = this.fragmentIndex
      console.info(`upload file total size: ${file.size}, fragment size: ${chunkSize}, fragment count: ${chunks}`)

      // 分块读取, 避免大文件内存溢出
      function loadNext() {
        const start = currentChunk * chunkSize
        let end
        if ((start + chunkSize) >= file.size) {
          lastFragment = 1
          end = file.size
        } else {
          end = start + chunkSize
        }

        fileReader.readAsArrayBuffer(blobSlice.call(file, start, end))
      }

      loadNext()

      return new Promise((resolve, reject) => {
        fileReader.onload = async(e) => {
          console.group('upload fragment')
          console.log('read chunk ', currentChunk + 1, 'of', chunks)
          let result
          try {
            result = await this.upload(e.target.result, currentChunk, lastFragment, file)
            console.log(result)
            const percent = Number(((currentChunk + 1) / chunks).toFixed(4)) * 100
            this.percentage = percent
            this.onProgress(percent, file)
            currentChunk++
          } catch (error) {
            return reject(error)
          }
          console.groupEnd('upload fragment')
          if (currentChunk < chunks) {
            loadNext()
          } else {
            // const md5 = spark.end()
            // console.log('finished loading')
            // console.info('computed hash', md5) // Compute hash
            // this.uploadData.md5 = md5 // '12a3' // md5
            resolve(result)
          }
        }

        fileReader.onerror = (error) => {
          // this.$message({
          //   type: 'error',
          //   message: `读取文件异常(${error.message})`,
          //   error
          // })
          reject(error)
        }
      })
    },

    upload(fileBuffer, currentChunk, lastFragment, origFile) {
      const file = new File([fileBuffer], origFile.name, { type: origFile.type, lastModified: Date.now() })
      const formData = new FormData()
      formData.append('file', file)
      formData.append('fragment_index', currentChunk + 1)
      formData.append('last_fragment', lastFragment)
      formData.append('md5', origFile.md5)

      return this.$http.post('mv/upload_mv', formData)
    },

    handleSuccess(resp, file, fileList) {
      const { onSuccess, onError } = this.$props
      if ('code' in resp && resp.code === STATUS_CODE.SUCCESS) {
        if (onSuccess && typeof onSuccess === 'function') {
          onSuccess(resp, file, fileList)
        }
      } else {
        onError(new Error(resp.msg), file)
      }
    },

    calMediaMD5(file) {
      const blobSlice = File.prototype.slice || File.prototype.mozSlice || File.prototype.webkitSlice
      const chunkSize = 10485760 // Read in chunks of 2MB
      const chunks = Math.ceil(file.size / chunkSize)
      const spark = new SparkMD5.ArrayBuffer()
      const fileReader = new FileReader()
      let currentChunk = 0

      function loadNext() {
        var start = currentChunk * chunkSize
        var end = ((start + chunkSize) >= file.size) ? file.size : start + chunkSize

        // 分块读取, 避免大文件内存溢出
        fileReader.readAsArrayBuffer(blobSlice.call(file, start, end))
      }

      loadNext()

      return new Promise((resolve, reject) => {
        fileReader.onload = (e) => {
        // console.log('read chunk nr', currentChunk + 1, 'of', chunks)
          spark.append(e.target.result) // Append array buffer
          currentChunk++

          if (currentChunk < chunks) {
            loadNext()
          } else {
            const md5 = spark.end()
            console.info('computed hash', md5) // Compute hash
            this.uploadData.md5 = md5 // '12a3' // md5
            resolve(md5)
          }
        }

        fileReader.onerror = (error) => {
          console.warn('oops, something went wrong.')
          // this.$message({
          //   type: 'error',
          //   message: '音频计算MD5异常'
          // })
          reject(error)
        }
      })
    },
  }
}
</script>

<style lang="scss">
.fragment-upload {
  .el-upload-dragger {
    border: none;
    width: auto;
    height: auto;
    text-align: left;
  }
  .el-upload {
    display: block;
  }

  .upload-status {

    .el-progress-bar {
      margin-right: -100px;
      padding-right: 100px;
    }
  }
}
</style>
